
import { useState } from "react";
import { format, addDays, isSameDay } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

interface DateTimeSelectorProps {
  selectedDate: Date | undefined;
  selectedTime: string;
  onDateSelect: (date: Date | undefined) => void;
  onTimeSelect: (time: string) => void;
}

export function DateTimeSelector({ 
  selectedDate, 
  selectedTime, 
  onDateSelect, 
  onTimeSelect 
}: DateTimeSelectorProps) {
  const timeSlots = [
    "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
    "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM"
  ];

  // Generate available slots for the selected date
  const getAvailableSlots = (date: Date | undefined) => {
    if (!date) return [];
    
    // Mock availability - in real app this would come from API
    const today = new Date();
    if (isSameDay(date, today)) {
      return timeSlots.slice(3); // Only afternoon slots for today
    }
    return timeSlots;
  };

  const availableSlots = getAvailableSlots(selectedDate);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-blue-600" />
            Select Date
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={onDateSelect}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today || date > addDays(today, 90);
            }}
            initialFocus
            className={cn("p-0 pointer-events-auto")}
          />
          <div className="mt-4 text-sm text-gray-600">
            <p>• Select any date within the next 90 days</p>
            <p>• Same-day appointments available until 2 PM</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Available Times
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedDate ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {format(selectedDate, "EEEE, MMMM d, yyyy")}
                </span>
                <Badge variant="secondary">
                  {availableSlots.length} slots available
                </Badge>
              </div>
              
              {availableSlots.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {availableSlots.map((time) => (
                    <Button
                      key={time}
                      variant={selectedTime === time ? "default" : "outline"}
                      size="sm"
                      onClick={() => onTimeSelect(time)}
                      className="justify-center"
                    >
                      {time}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No slots available for this date</p>
                  <p className="text-sm text-gray-400 mt-1">Please select a different date</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Please select a date first</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
